import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mail, Send, Sparkles } from "lucide-react";

export const PregnancyReminderDialog = ({ open, onOpenChange, fiscalCode, patientName, onSent }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null);
  const [text, setText] = useState("");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");

  useEffect(() => {
    if (!open) return;
    setDraft(null); setText(""); setLoading(true);
    api.post("/patients/pregnancy-reminder/draft", { fiscal_code: fiscalCode })
      .then(({ data }) => { setDraft(data); setText(data.text); setTo(data.to_email || ""); setSubject(data.subject); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Impossibile preparare il promemoria"))
      .finally(() => setLoading(false));
  }, [open, fiscalCode]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/patients/pregnancy-reminder/send",
        { fiscal_code: fiscalCode, to_email: to, subject, text });
      toast.success(data.sent ? `Promemoria inviato a ${to}` : "Promemoria registrato (invio email non attivo)");
      onOpenChange(false); onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pregnancy-reminder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4 text-pink-600" /> Promemoria a {patientName}</DialogTitle>
          <DialogDescription>
            <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-indigo-500" /> Testo proposto dall'AI in base ai controlli dovuti: puoi modificarlo liberamente prima dell'invio.</span>
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-slate-500">L'AI sta scrivendo il messaggio…</div>
        ) : draft ? (
          <div className="space-y-3">
            {draft.checks?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {draft.checks.map((c) => <span key={c} className="text-[11px] rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5">{c}</span>)}
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600">Email paziente</label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="paziente@email.it" data-testid="pregnancy-reminder-to" />
              {!draft.to_email && <p className="text-[11px] text-amber-700 mt-1">Nessuna email registrata nelle visite: inseriscila manualmente.</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Oggetto</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="pregnancy-reminder-subject" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Messaggio</label>
              <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} data-testid="pregnancy-reminder-text" />
              <p className="text-[11px] text-slate-400 mt-1">Saluto iniziale e firma vengono aggiunti automaticamente.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
              <Button onClick={send} disabled={sending || !to.includes("@") || !text.trim()}
                      className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="pregnancy-reminder-send">
                <Send className="h-4 w-4 mr-1.5" /> {sending ? "Invio…" : "Invia email"}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
